import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty } from '../ui'
import { getNFLMarket } from '../api'

// Game Lines — moneyline / spread / total per game, with line MOVEMENT (open → now) and the
// Pinnacle-vs-public (sharp-vs-square) gap. Line movement is the public handle's footprint:
// books move the number to balance money, so a moving line shows where the money went. Pinnacle
// (the sharpest book) disagreeing with the public books flags smart money.

const sgn = v => (v > 0 ? `+${v}` : `${v}`)                 // american odds / signed number
const favLine = sp => (sp == null ? '—' : `-${Math.abs(sp)}`)  // favorite always shows a minus

function Move({ v, unit = '' }) {
  if (v == null || v === 0) return <span style={{ color: T.faint, fontWeight: 700 }}>no move</span>
  const up = v > 0
  return (
    <span style={{ color: up ? T.green : T.amber, fontWeight: 800, fontFamily: T.mono }}>
      {up ? '▲' : '▼'} {Math.abs(v)}{unit}
    </span>
  )
}

function Chip({ text, color }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.04em', color,
      border: `1px solid ${color}66`, borderRadius: 999, padding: '2px 8px' }}>{text}</span>
  )
}

function GameCard({ g }) {
  const sp = g.spread || {}, tot = g.total || {}, ml = g.moneyline || {}
  const steam = g.signals?.includes('spread_steam') || g.signals?.includes('total_steam')
  const sharp = g.signals?.includes('sharp_spread_disagree')
  // Sharp side: Pinnacle hangs a bigger number on a team than the public = sharp lean that way.
  const sharpSide = (sp.sharp_gap != null && sp.sharp_gap !== 0)
    ? (sp.sharp_gap < 0 ? g.home : g.away) : null
  return (
    <div style={{ background: T.card, border: `1px solid ${steam || sharp ? T.greenMid : T.border}`,
      borderRadius: 13, padding: '13px 14px', marginBottom: 9 }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 900 }}>
          {g.away} <span style={{ color: T.muted, fontWeight: 700 }}>@</span> {g.home}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
          {steam && <Chip text="STEAM" color={T.green} />}
          {sharp && <Chip text="SHARP SPLIT" color={T.blue} />}
        </div>
      </div>

      {/* spread */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
        borderTop: `1px solid ${T.border}` }}>
        <div style={{ width: 54, fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.04em' }}>SPREAD</div>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 800 }}>
          {sp.favorite || '—'} <span style={{ fontFamily: T.mono }}>{favLine(sp.current)}</span>
          {sp.open != null && sp.current != null && sp.open !== sp.current &&
            <span style={{ fontSize: 11, color: T.faint, fontWeight: 600 }}>  (open {favLine(sp.open)})</span>}
        </div>
        <Move v={sp.move} />
        {sp.money_side && sp.move ? <span style={{ fontSize: 10.5, color: T.muted, fontWeight: 700 }}>→ {sp.money_side}</span> : null}
      </div>

      {/* total */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
        borderTop: `1px solid ${T.border}` }}>
        <div style={{ width: 54, fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.04em' }}>TOTAL</div>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 800, fontFamily: T.mono }}>
          {tot.current != null ? tot.current : '—'}
          {tot.open != null && tot.current != null && tot.open !== tot.current &&
            <span style={{ fontSize: 11, color: T.faint, fontWeight: 600 }}>  (open {tot.open})</span>}
        </div>
        <Move v={tot.move} />
      </div>

      {/* moneyline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0 2px',
        borderTop: `1px solid ${T.border}` }}>
        <div style={{ width: 54, fontSize: 11, fontWeight: 800, color: T.muted, letterSpacing: '.04em' }}>ML</div>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 800, fontFamily: T.mono }}>
          {g.away} {ml.away != null ? sgn(ml.away) : '—'} <span style={{ color: T.faint }}>/</span> {g.home} {ml.home != null ? sgn(ml.home) : '—'}
        </div>
      </div>

      {/* public money — Action Network ticket% vs money% (the handle) */}
      {g.splits && (() => {
        const s = g.splits
        const pc = v => v == null ? '—' : v + '%'
        const SHARP = { spreadHome: `${g.home} spread`, spreadAway: `${g.away} spread`,
          moneylineHome: `${g.home} ML`, moneylineAway: `${g.away} ML`, over: 'Over', under: 'Under' }
        const row = (label, a, b, aLbl, bLbl) => {
          if (!a && !b) return null
          const lead = (a?.money ?? -1) >= (b?.money ?? -1) ? 'a' : 'b'
          const gap = (lead === 'a' ? a : b)?.gap
          const big = gap != null && Math.abs(gap) >= 10
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '2px 0' }}>
              <div style={{ width: 54, fontSize: 10, fontWeight: 800, color: T.faint, letterSpacing: '.04em' }}>{label}</div>
              <div style={{ flex: 1, fontFamily: T.mono }}>
                <span style={{ color: lead === 'a' ? T.text : T.faint, fontWeight: lead === 'a' ? 800 : 600 }}>{aLbl} {pc(a?.money)}</span>
                <span style={{ color: T.faint }}> / </span>
                <span style={{ color: lead === 'b' ? T.text : T.faint, fontWeight: lead === 'b' ? 800 : 600 }}>{bLbl} {pc(b?.money)}</span>
                <span style={{ color: T.faint, fontSize: 10 }}> money</span>
              </div>
              {big && <span style={{ fontSize: 10, fontWeight: 800, color: T.blue }}>◆ sharp</span>}
            </div>
          )
        }
        return (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: '.06em', marginBottom: 2 }}>
              💵 PUBLIC MONEY{s.bet_count ? <span style={{ color: T.faint, fontWeight: 600 }}> · {Math.round(s.bet_count / 1000)}k bets</span> : null}
            </div>
            {row('SPREAD', s.spread?.away, s.spread?.home, g.away, g.home)}
            {row('TOTAL', s.total?.over, s.total?.under, 'O', 'U')}
            {row('ML', s.moneyline?.away, s.moneyline?.home, g.away, g.home)}
            {s.sharp_side && SHARP[s.sharp_side] &&
              <div style={{ fontSize: 10, color: T.blue, fontWeight: 800, marginTop: 3 }}>◆ Action Network sharp side: {SHARP[s.sharp_side]}</div>}
          </div>
        )
      })()}

      {/* sharp read + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
        fontSize: 10.5, color: T.muted, fontWeight: 600 }}>
        {g.has_pinnacle && sp.pinnacle != null
          ? <span>Sharp (Pinnacle) <b style={{ color: T.blue }}>{favLine(sp.pinnacle)}</b> vs public <b>{favLine(sp.public)}</b>
              {sharpSide ? <span style={{ color: T.blue, fontWeight: 800 }}> · sharp on {sharpSide}</span> : null}</span>
          : <span style={{ color: T.faint }}>Pinnacle not in feed — add <code>eu</code> region for the sharp read</span>}
        <span style={{ marginLeft: 'auto', color: T.faint }}>{g.books} books</span>
      </div>
    </div>
  )
}

export default function GameLinesTab({ season = 2026, week = 1 }) {
  const [d, setD] = useState(null)
  useEffect(() => { let a = true; setD(null)
    getNFLMarket(season, week).then(r => { if (a) setD(r) }); return () => { a = false } }, [season, week])

  if (!d) return <Spinner />
  const games = (d.games || []).slice().sort((a, b) => {
    // Action first: games with signals on top, then by spread-move magnitude.
    const sa = (a.signals?.length || 0) > 0 ? 1 : 0, sb = (b.signals?.length || 0) > 0 ? 1 : 0
    if (sa !== sb) return sb - sa
    return Math.abs(b.spread?.move || 0) - Math.abs(a.spread?.move || 0)
  })

  return (
    <div style={{ padding: '10px 14px 80px' }}>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.green}`,
        borderRadius: 11, padding: '11px 13px', marginBottom: 12, fontSize: 12, color: T.muted2, lineHeight: 1.5 }}>
        <b style={{ color: T.green }}>Where the money's going.</b> Books move the line to balance the money,
        so <b style={{ color: T.text }}>line movement is the handle's footprint</b>. ▲/▼ = open → now.
        <b style={{ color: T.blue }}> Pinnacle</b> (the sharpest book) disagreeing with the public books flags smart money.
      </div>
      {games.length === 0
        ? <Empty message="No game-odds snapshots yet — movement builds as the week's lines are pulled." />
        : games.map(g => <GameCard key={g.game_id} g={g} />)}
    </div>
  )
}
